import { OnboardingApps } from '@app/context/constants';
import {
  OnboardingState,
  useOnboardingActions,
  useOnboardingStateMachine,
} from '@app/context/OnboardingStateMachineContext';
import { useUser } from '@app/context/UserContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { attributionVariables, captureAttribution } from '@app/utils/attribution';
import { Chat } from '@components/block/Chat/Chat';
import { Locales } from '@components/block/Header/LangSelect';
import { Loading } from '@components/ui';
import { useUserCreateMutation } from '@dieterApi/user/useUserCreateMutation';
import { useUserLoginMutation } from '@dieterApi/user/useUserLoginMutation';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

// type as generic to avoid type errors
const AppIdUrlMap: {
  [key: string]: OnboardingApps;
} = {
  checkup: OnboardingApps.CHECKUP,
  datenschutzerklaerung: OnboardingApps.DSE,
  toms: OnboardingApps.TOMs,
  impressum: OnboardingApps.IMPRINT,
  vvt: OnboardingApps.VVT,
  avv: OnboardingApps.AVV,
  auftragsverarbeitung: OnboardingApps.AVV, // alias used by landing page CTAs
  info: OnboardingApps.INFO,
  dsfa: OnboardingApps.DSFA,
  mitarbeiterverpflichtung: OnboardingApps.EMPLOYEE_COMMITMENT,
  'website-scan': OnboardingApps.WEBSITE_SCAN,
};

export function Onboarding() {
  const { i18n } = useTranslation();
  const { user, userLoading } = useUser();
  const {
    setNavigation,
    navigation: { scope, stateMachine },
  } = useNavigation();
  const { topics, questionnaires, whitelabelConfig } = useUserWithCompany();
  const {
    params: { appTitleUrl },
  } = useRouteMatch<{ appTitleUrl?: string }>();
  const history = useHistory();

  const avatarImage =
    whitelabelConfig?.avatarB64 && whitelabelConfig.avatarB64 !== 'unset'
      ? whitelabelConfig.avatarB64
      : '/assets/images/dieter_winkt.svg';

  const { currentState } = useOnboardingStateMachine();
  const { selectApp, accountCreateRequested } = useOnboardingActions();

  const { enqueueSnackbar } = useSnackbar();
  const [createUser, { loading: createUserLoading }] = useUserCreateMutation();

  const appId = AppIdUrlMap[appTitleUrl || 'checkup'];
  const [login, { loading: loginLoading }] = useUserLoginMutation();

  const loading = createUserLoading || loginLoading || userLoading;

  // Get query parameter "authtoken" from URL
  const queryParams = new URLSearchParams(location.search);
  const authtoken = queryParams.get('authtoken');
  // When a user comes from Wix we need to get the wix_id to link the user
  const wixId = queryParams.get('wix_id');
  // this is for whitelabel customer onboarding
  const whitelabelConfigId = queryParams.get('wlc_id');
  // when a user is a cello referral
  const celloReferralCode = queryParams.get('ucc');
  const celloProductId = queryParams.get('productId');
  // when a user comes from a referral partner
  const referralPartnerId = queryParams.get('partner_id');
  // when a user comes from the landing page website scan
  const scanJobId = queryParams.get('scan_id');

  // Get first two letters of language code
  const langString = i18n.language.slice(0, 2) as Locales;

  // mount and unmount effects to set and unset navigation scope
  useEffect(() => {
    return () => setNavigation((nav) => void (nav.scope = undefined));
  }, []);

  useEffect(() => {
    if (scope !== 'onboarding') {
      setNavigation((nav) => void (nav.scope = 'onboarding'));
    }
  }, [scope]);

  useEffect(() => {
    // If authtoken is present, use it to log in the user
    if (authtoken && authtoken !== 'without') {
      login({
        variables: {
          email: '',
          password: '',
          token: authtoken,
          locale: langString,
        },
      }).catch((e) =>
        // TODO/WM/ASK: it will be better for "multi lang" to map error codes with messages
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack })
      );
    }
  }, [authtoken]);

  useEffect(() => {
    if (!user) return;

    // Handle app selection when arriving with URL parameter or whitelabel config
    if (appTitleUrl || whitelabelConfigId) {
      const app = topics.flatMap((t) => t.applications).find((a) => a.id === (appId || 'app-checkup'));
      if (app) {
        // send view_item event to GA4
        if (window.dataLayer) {
          let value: number;
          switch (app?.id) {
            case 'app-checkup':
              value = 348;
              break;
            case 'app-dse-privacy-policy-create':
              value = 84;
              break;
            default:
              value = 180;
              break;
          }

          window.dataLayer.push({
            event: 'ga4-event-view-item',
            ordervalue: value,
            email: user?.email,
          });
        }

        // Trigger app selection via state machine
        if (currentState === OnboardingState.OnboardingChatActive) {
          selectApp(app.id);
        }
      }
    }
  }, [user, appTitleUrl, whitelabelConfigId, topics, currentState, selectApp]);

  // Website-scan flow: skip chat and questionnaire, go directly to email input
  useEffect(() => {
    if (!user) return;
    if (appId !== OnboardingApps.WEBSITE_SCAN) return;
    if (currentState === OnboardingState.OnboardingChatActive) {
      accountCreateRequested(OnboardingApps.WEBSITE_SCAN);
    }
  }, [user, appId, currentState, accountCreateRequested]);

  useEffect(() => {
    // Skip onboarding if this is not the first company of a user or there are already questionnaires
    if ((user?.companies.length || 0) > 1 || stateMachine?.state === OnboardingState.Complete) {
      if (appId === OnboardingApps.WEBSITE_SCAN) {
        history.push('/dashboard/topic/dse/technology-scan');
      } else {
        history.push('/dashboard');
      }
    }
  }, [user?.companies.length, stateMachine?.state, history, appId]);

  // Create anonymous user when entering the onboarding process
  useEffect(() => {
    if (loading) return;
    if (!user && !authtoken) {
      // Herkunft (gclid, utm_*, Landingpage, Referrer) sicherheitshalber noch einmal
      // aus der aktuellen URL lesen, dann mit dem Account speichern (utils/attribution.ts)
      captureAttribution();
      createUser({
        variables: {
          locale: i18n.language.slice(0, 2) as Locales,
          wixId: wixId || undefined,
          whitelabelConfigId: whitelabelConfigId || undefined,
          celloReferralCode: celloReferralCode || undefined,
          referralPartnerId: referralPartnerId || undefined,
          scanJobId: scanJobId || undefined,
          ...attributionVariables(),
        },
      }).catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
    }
  }, [user, loading]);

  return !user || loading || appTitleUrl ? (
    <>
      <div className="flex items-center justify-center mt-24">
        <Loading />
      </div>
    </>
  ) : (
    <div>
      <img
        src={avatarImage}
        alt="Dieter winkt"
        className="max-h-[70vh] h-[600px] absolute bottom-20 left-1/2 -translate-x-1/2"
      />
      <Chat />
    </div>
  );
}
